import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Lock, CheckCircle2, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API_BASE, formatApiError } from "@/lib/api";
import { LOGO_URL, COMPANY } from "@/lib/constants";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const err = useMemo(() => {
    if (!token) return "Kein Reset-Token gefunden. Bitte fordern Sie einen neuen Link an.";
    if (pw1 && pw1.length < 8) return "Passwort muss mindestens 8 Zeichen haben.";
    if (pw1 && pw2 && pw1 !== pw2) return "Die beiden Passwörter stimmen nicht überein.";
    return null;
  }, [token, pw1, pw2]);

  async function onSubmit(e) {
    e.preventDefault();
    if (err || !pw1) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/auth/password-reset/confirm`, {
        token, new_password: pw1,
      });
      setDone(true);
      setTimeout(() => nav("/login"), 2400);
    } catch (er) {
      toast.error(formatApiError(er));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (done) toast.success("Passwort erfolgreich geändert. Bitte melden Sie sich neu an.");
  }, [done]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#FAFAF5]">
      <div className="w-full max-w-sm">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-[#5C6B5D] hover:text-[#6B7F3E] mb-8" data-testid="reset-back-link">
          <ArrowLeft className="w-4 h-4" /> Zum Login
        </Link>
        <div className="flex items-center gap-3 mb-8">
          <img src={LOGO_URL} alt="" className="h-12 w-auto" />
          <div>
            <div className="font-display text-lg font-semibold text-[#1A201A]">{COMPANY.name}</div>
            <div className="text-xs text-[#6B7F3E]">Neues Passwort setzen</div>
          </div>
        </div>

        {done ? (
          <div data-testid="reset-success" className="bg-white rounded-2xl border border-[#E5E7EB] p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-[#E8ECE1] text-[#6B7F3E] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7" strokeWidth={1.5} />
            </div>
            <h2 className="font-display text-lg font-semibold text-[#1A201A] mb-2">Passwort geändert</h2>
            <p className="text-sm text-[#5C6B5D]">Sie werden gleich zum Login weitergeleitet …</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-semibold text-[#1A201A] tracking-tight mb-2">
              Neues Passwort
            </h1>
            <p className="text-sm text-[#5C6B5D] mb-6">
              Bitte wählen Sie ein sicheres Passwort mit mindestens 8 Zeichen.
            </p>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="pw1">Neues Passwort</Label>
                <Input
                  id="pw1" data-testid="reset-password-input" type="password" required
                  value={pw1} onChange={(e) => setPw1(e.target.value)}
                  className="mt-1.5" autoComplete="new-password"
                />
              </div>
              <div>
                <Label htmlFor="pw2">Passwort wiederholen</Label>
                <Input
                  id="pw2" data-testid="reset-password-confirm-input" type="password" required
                  value={pw2} onChange={(e) => setPw2(e.target.value)}
                  className="mt-1.5" autoComplete="new-password"
                />
              </div>
              {err && (
                <div className="flex items-start gap-2 text-xs text-[#7A2E14] bg-[#FDF2EA] border border-[#F5C874] rounded-lg p-2.5">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{err}</span>
                </div>
              )}
              <Button
                type="submit" data-testid="reset-submit-btn"
                disabled={busy || !!err || !pw1 || !pw2}
                className="w-full bg-[#6B7F3E] hover:bg-[#5A6B34] text-white h-10"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-1.5" /> Passwort speichern</>}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
